import React from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/interviews", label: "Interviews" },
  { to: "/resources", label: "Resources" },
  { to: "/about", label: "About" },
  { to: "/join", label: "Join" },
];

const Monogram = () => (
  <div className="flex items-center gap-3" data-testid="brand-mark">
    <img src="/logo.png" alt="Tomorrow's Doctors" className="h-11 w-auto" />
  </div>
);

export const Header = () => {
  const [open, setOpen] = React.useState(false);
  return (
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-sm" data-testid="site-header">
      <div className="container-wide flex items-center justify-between h-20">
        <Link to="/" data-testid="brand-link"><Monogram /></Link>

        <nav className="hidden md:flex items-center gap-9">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-[11px] uppercase tracking-[0.24em] transition-colors ${
                  isActive ? "text-medblue" : "text-navy/70 hover:text-navy"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link to="/join" className="hidden md:inline-flex btn-primary" data-testid="header-cta">
            Become An Interviewee
          </Link>
          <button
            aria-label="Toggle menu"
            className="md:hidden p-2 text-navy"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      {open && (
        <div className="md:hidden border-t border-rule bg-white" data-testid="mobile-menu">
          <div className="container-wide py-4 flex flex-col gap-4">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="text-sm uppercase tracking-[0.2em] text-navy">
                {n.label}
              </Link>
            ))}
            <Link to="/join" onClick={() => setOpen(false)} className="btn-primary self-start">Become An Interviewee</Link>
          </div>
        </div>
      )}
    </header>
  );
};

export const Footer = () => (
  <footer className="bg-navy text-white" data-testid="site-footer">
    <div className="container-wide pt-16 pb-8">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-10 border-t border-white/10 pt-14">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-white/50 mb-4">Community Pulse</div>
          <div className="font-serif text-2xl text-medgreen">Launching Soon</div>
          <div className="mt-2 text-sm text-white/60">Archive currently being built</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-white/50 mb-4">Explore</div>
          <ul className="space-y-2 text-sm text-white/80">
            <li><Link to="/interviews" className="hover:text-medblue transition-colors">Interview Archive</Link></li>
            <li><Link to="/resources" className="hover:text-medblue transition-colors">Student Resources</Link></li>
            <li><Link to="/about" className="hover:text-medblue transition-colors">Our Mission</Link></li>
            <li><Link to="/join" className="hover:text-medblue transition-colors">Join &amp; Contact</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-white/50 mb-4">Categories</div>
          <ul className="space-y-2 text-sm text-white/80">
            <li><Link to="/interviews?category=Physicians" className="hover:text-medblue transition-colors">Physicians</Link></li>
            <li><Link to="/interviews?category=Researchers" className="hover:text-medblue transition-colors">Researchers</Link></li>
            <li><Link to="/interviews?category=Healthcare%20Founders" className="hover:text-medblue transition-colors">Healthcare Founders</Link></li>
            <li><Link to="/interviews?category=Public%20Health%20Professionals" className="hover:text-medblue transition-colors">Public Health Leaders</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-white/50 mb-4">Contact</div>
          <p className="text-sm text-white/70 leading-relaxed">
            For partnerships and press, reach out through our contact page.
          </p>
          <Link to="/join" className="inline-flex items-center gap-1 mt-3 text-xs uppercase tracking-[0.2em] text-medblue hover:text-white transition-colors">
            Get in touch →
          </Link>
        </div>
      </div>
      <div className="mt-14 pt-6 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
        <div className="font-serif text-lg text-white">Tomorrow&rsquo;s Doctors</div>
        <div className="text-[10px] uppercase tracking-[0.24em] text-white/50">
          &copy; {new Date().getFullYear()} Tomorrow&rsquo;s Doctors · A Healthcare Education Archive
        </div>
      </div>
    </div>
  </footer>
);

export default function Layout({ children }) {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");
  return (
    <div className="min-h-screen flex flex-col bg-white">
      {!isAdmin && <Header />}
      <main className="flex-1">{children}</main>
      {!isAdmin && <Footer />}
    </div>
  );
}
